import React, { useState } from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Link,
  Alert,
  Divider
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface SetupInstructionsProps {
  integrationType: string;
}

interface SetupStep {
  label: string;
  content: React.ReactNode;
}

const setupInstructions: Record<string, {
  title: string;
  description: string;
  steps: SetupStep[];
  additionalInfo?: React.ReactNode;
}> = {
  imap: {
    title: 'IMAP Email Setup',
    description: 'Configure IMAP access for your email account',
    steps: [
      {
        label: 'Enable IMAP in your email provider',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              Most email providers require you to enable IMAP access:
            </Typography>
            <ul>
              <li><strong>Gmail:</strong> Settings → Forwarding and POP/IMAP → Enable IMAP</li>
              <li><strong>Outlook.com:</strong> Settings → Sync email → Enable POP and IMAP</li>
              <li><strong>Yahoo:</strong> Settings → Mailboxes → IMAP access</li>
            </ul>
          </div>
        )
      },
      {
        label: 'Get your IMAP server settings',
        content: (
          <div>
            <Typography variant="body2" paragraph>Common IMAP settings:</Typography>
            <div style={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', padding: 8, borderRadius: 4, marginBottom: 16 }}>
              <strong>Gmail:</strong><br />
              Server: imap.gmail.com<br />
              Port: 993 (SSL/TLS)<br />

              <Divider style={{ marginTop: 8, marginBottom: 8 }} />

              <strong>Outlook/Office 365:</strong><br />
              Server: outlook.office365.com<br />
              Port: 993 (SSL/TLS)<br />

              <Divider style={{ marginTop: 8, marginBottom: 8 }} />

              <strong>Yahoo:</strong><br />
              Server: imap.mail.yahoo.com<br />
              Port: 993 (SSL/TLS)
            </div>
          </div>
        )
      },
      {
        label: 'Handle Two-Factor Authentication (2FA)',
        content: (
          <div>
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              If you have 2FA enabled, you'll need an app-specific password instead of your regular password.
            </Alert>
            <Typography variant="body2" paragraph>
              <strong>For Microsoft/Office 365:</strong>
            </Typography>
            <ol>
              <li>Go to <Link href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener">
                Microsoft Security <OpenInNewIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
              </Link></li>
              <li>Click "Add method" → "App password"</li>
              <li>Name it (e.g., "Prismatic IMAP")</li>
              <li>Copy the generated password</li>
              <li>Use this app password instead of your regular password</li>
            </ol>

            <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
              <strong>For Gmail:</strong>
            </Typography>
            <ol>
              <li>Go to <Link href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener">
                Google App Passwords <OpenInNewIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
              </Link></li>
              <li>Select "Mail" and your device</li>
              <li>Copy the generated password</li>
              <li>Use this app password instead of your regular password</li>
            </ol>
          </div>
        )
      },
      {
        label: 'Enter your credentials',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              Fill in the form with:
            </Typography>
            <ul>
              <li><strong>Connection Name:</strong> A friendly name for this connection</li>
              <li><strong>Email Address:</strong> Your full email address</li>
              <li><strong>IMAP Server:</strong> From step 2</li>
              <li><strong>IMAP Port:</strong> Usually 993 for SSL/TLS</li>
              <li><strong>Username:</strong> Usually your full email address</li>
              <li><strong>Password:</strong> Your password or app-specific password from step 3</li>
            </ul>
          </div>
        )
      }
    ],
    additionalInfo: (
      <Alert severity="info">
        SMTP settings are optional and only needed if you want to send emails through Prismatic.
      </Alert>
    )
  },
  microsoft_exchange: {
    title: 'Microsoft Exchange Email Setup',
    description: 'Connect to Microsoft Exchange/Office 365 using OAuth',
    steps: [
      {
        label: 'Ensure you have the right account type',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              This integration works with:
            </Typography>
            <ul>
              <li>Microsoft 365 Business accounts</li>
              <li>Office 365 accounts</li>
              <li>Exchange Online accounts</li>
              <li>Outlook.com personal accounts</li>
            </ul>
          </div>
        )
      },
      {
        label: 'Click "Connect with Microsoft"',
        content: (
          <Typography variant="body2">
            You'll be redirected to Microsoft's login page. This is secure - Prismatic never sees your password.
          </Typography>
        )
      },
      {
        label: 'Sign in and grant permissions',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              Microsoft will ask you to grant Prismatic permission to:
            </Typography>
            <ul>
              <li>Read your email</li>
              <li>Send email on your behalf</li>
              <li>Access your calendar (if applicable)</li>
            </ul>
            <Typography variant="body2" style={{ marginTop: 16 }}>
              You can revoke these permissions anytime from your Microsoft account settings.
            </Typography>
          </div>
        )
      }
    ],
    additionalInfo: (
      <Alert severity="success">
        OAuth is the recommended method as it supports 2FA and doesn't require storing passwords.
      </Alert>
    )
  },
  gmail: {
    title: 'Gmail Setup',
    description: 'Connect to Gmail using OAuth',
    steps: [
      {
        label: 'Click "Connect with Google"',
        content: (
          <Typography variant="body2">
            You'll be redirected to Google's login page. This is secure - Prismatic never sees your password.
          </Typography>
        )
      },
      {
        label: 'Choose your Google account',
        content: (
          <Typography variant="body2">
            Select the Gmail account you want to connect. You can connect multiple accounts by repeating this process.
          </Typography>
        )
      },
      {
        label: 'Grant permissions',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              Google will ask you to grant Prismatic permission to:
            </Typography>
            <ul>
              <li>Read, compose, and send emails</li>
              <li>Access your email metadata</li>
            </ul>
            <Alert severity="info" style={{ marginTop: 16 }}>
              Prismatic only syncs emails from the last year to your knowledge graph.
            </Alert>
          </div>
        )
      }
    ]
  },
  microsoft: {
    title: 'Microsoft Graph Setup',
    description: 'Full Microsoft 365 integration',
    steps: [
      {
        label: 'Administrator consent may be required',
        content: (
          <Alert severity="warning">
            For business accounts, your IT administrator may need to approve this integration first.
          </Alert>
        )
      },
      {
        label: 'Click "Connect with Microsoft"',
        content: (
          <Typography variant="body2">
            You'll be redirected to Microsoft's login page.
          </Typography>
        )
      },
      {
        label: 'Grant comprehensive permissions',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              This integration requests access to:
            </Typography>
            <ul>
              <li>Email (read and send)</li>
              <li>Calendar (read and write)</li>
              <li>OneDrive files</li>
              <li>Teams messages (if applicable)</li>
              <li>Contacts</li>
            </ul>
          </div>
        )
      }
    ],
    additionalInfo: (
      <Alert severity="info">
        This is the most comprehensive Microsoft integration. For email-only access, consider using Microsoft Exchange Email instead.
      </Alert>
    )
  },
  google: {
    title: 'Google Workspace Setup',
    description: 'Full Google Workspace integration',
    steps: [
      {
        label: 'Workspace account recommended',
        content: (
          <Typography variant="body2">
            While this works with personal Gmail accounts, it's designed for Google Workspace (formerly G Suite) accounts.
          </Typography>
        )
      },
      {
        label: 'Click "Connect with Google"',
        content: (
          <Typography variant="body2">
            You'll be redirected to Google's login page.
          </Typography>
        )
      },
      {
        label: 'Grant comprehensive permissions',
        content: (
          <div>
            <Typography variant="body2" paragraph>
              This integration requests access to:
            </Typography>
            <ul>
              <li>Gmail (full access)</li>
              <li>Google Calendar</li>
              <li>Google Drive files</li>
              <li>Google Contacts</li>
              <li>Google Meet (if applicable)</li>
            </ul>
          </div>
        )
      }
    ],
    additionalInfo: (
      <Alert severity="info">
        For email-only access, consider using the Gmail integration instead.
      </Alert>
    )
  }
};

export default function SetupInstructions({ integrationType }: SetupInstructionsProps) {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const instructions = setupInstructions[integrationType];

  if (!instructions) {
    return null;
  }


  return (
    <>
      <IconButton
        size="small"
        onClick={() => setOpen(true)}
        style={{ marginLeft: 8 }}
        title="Setup instructions"
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {instructions.title}
          <Typography variant="body2" color="text.secondary">
            {instructions.description}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {instructions.steps.map((step, index) => (
              <Step key={index} expanded>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {step.content}
                  <div style={{ marginTop: 16 }}>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(index + 1)}
                      disabled={index === instructions.steps.length - 1}
                      size="small"
                    >
                      Next
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={() => setActiveStep(index - 1)}
                      size="small"
                      style={{ marginLeft: 8 }}
                    >
                      Back
                    </Button>
                  </div>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === instructions.steps.length && (
            <div style={{ marginTop: 24 }}>
              <Alert severity="success">
                Setup complete! Close this dialog and proceed with the connection.
              </Alert>
              <Button
                onClick={() => setActiveStep(0)}
                style={{ marginTop: 16 }}
              >
                Start over
              </Button>
            </div>
          )}

          {instructions.additionalInfo && (
            <div style={{ marginTop: 24 }}>
              {instructions.additionalInfo}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
